import Box from "@material-ui/core/Box";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import DropZoneIcon from "@material-ui/icons/CloudUploadRounded";
import clsx from "clsx";
import React, { ChangeEvent, createRef, DragEvent, MouseEvent, useCallback } from "react";
import { useBooleanState } from "../../hook";
import { Color } from "../../theme";
import { HasTestId, PlainFunctionComponent } from "../../types";
import { identity, ifDefined } from "../../util";

export interface UploadDropZoneProps extends HasTestId {
    disabled?: boolean;
    dragAndDropDisabled?: boolean;
    fileInputProps?: {
        /**
         * Specifies the file types which to be selectable in the `<input>` (`type="file"`) dialog. Defaults to `*.*`.
         */
        accept?: string;
        /**
         * Determines if one (= `false`) or multiple (= `true`) files can be chosen in the dialog of the `<input>` (`type="file"`) dialog. Defaults to `true`.
         */
        multiple?: boolean;
    };
    height?: number | string;
    helperText?: string;
    label: string;
    linkedLabel?: string;
    onAppendFiles: (files: File[]) => void;
    required?: boolean;
}

export const UploadDropZone: PlainFunctionComponent<UploadDropZoneProps> = ({
    disabled,
    dragAndDropDisabled,
    fileInputProps,
    height,
    helperText,
    label,
    linkedLabel,
    onAppendFiles,
    required,
    testId,
}) => {
    const fileInputRef = createRef<HTMLInputElement>();

    const [draggedOver, onDragLeave, onDragEnterState] = useBooleanState(false);

    const onDragOver = useCallback(
        (event: DragEvent<HTMLDivElement>): void => {
            if (dragAndDropDisabled || disabled) {
                return;
            }
            event.preventDefault(); // required because otherwise the browser opens the files on drop
        },
        [dragAndDropDisabled, disabled],
    );

    const onDragEnter = useCallback(
        (event: DragEvent<HTMLDivElement>): void => {
            if (dragAndDropDisabled || disabled) {
                return;
            }
            event.preventDefault();
            onDragEnterState();
        },
        [dragAndDropDisabled, disabled, onDragEnterState],
    );

    const onDrop = useCallback(
        (event: DragEvent<HTMLDivElement>): void => {
            event.preventDefault();
            if (dragAndDropDisabled || disabled) {
                return;
            }

            // Do not further use very unhandy `FileList` as we cannot construct it anyhow; `Array.from` is ES2015 therefore using `Array.prototype.slice` instead for ES5
            onAppendFiles(Array.prototype.slice.call(event.dataTransfer.files));
            onDragLeave();
        },
        [dragAndDropDisabled, disabled, onAppendFiles, onDragLeave],
    );

    const onDropZoneClick = useCallback((): void => {
        if (disabled) {
            return;
        }
        ifDefined(fileInputRef.current, (c) => c.click());
    }, [disabled, fileInputRef]);

    const onInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            if (disabled) {
                return;
            }
            // Do not further use very unhandy `FileList` as we cannot construct it anyhow
            onAppendFiles(ifDefined(event.target.files, (f) => Array.prototype.slice.call(f), []));
        },
        [onAppendFiles],
    );

    const resetInputValueOnClick = useCallback((event: MouseEvent<HTMLInputElement>): void => {
        /*
            This allows a user to upload the same filename over and over again.
            This would have not been possible without this piece of code.

            E.g. Alice uploads:
            1. cute-cat.png // WORKS
            2. cute-dog.png // WORKS
            3. cute-dog.png // Won't trigger an upload
         */
        const uploadField = event.target as HTMLInputElement;
        uploadField.value = "";
    }, []);

    const classes = useStyles();
    return (
        <Box data-testid={testId}>
            <Box
                className={clsx(classes.dropZone, {
                    [classes.dropZoneDragOver]: draggedOver,
                    [classes.dropZoneFocusAndHover]: !disabled,
                    [classes.dropZonePointer]: !disabled,
                    [classes.dropZoneRequired]: required && !draggedOver,
                })}
                display="flex"
                justifyContent="center"
                alignContent="center"
                height={height}
                onDragEnter={dragAndDropDisabled ? undefined : onDragEnter}
                onDragOver={dragAndDropDisabled ? undefined : onDragOver}
                onDragLeave={dragAndDropDisabled ? undefined : onDragLeave}
                onDrop={dragAndDropDisabled ? undefined : onDrop}
                onClick={onDropZoneClick}
            >
                <input
                    accept={fileInputProps?.accept}
                    className={classes.fileInputField}
                    // TODO make it configurable from outside?
                    data-testid="dropZone-file-input"
                    disabled={disabled}
                    multiple={ifDefined(fileInputProps?.multiple, identity, true)}
                    onChange={onInputChange}
                    onClick={resetInputValueOnClick}
                    ref={fileInputRef}
                    type="file"
                />
                <Box className={classes.dropZoneTextures}>
                    <DropZoneIcon className={clsx(classes.dropZoneIcon, { [classes.dropZoneIconDisabled]: disabled })} />
                    <Box className={clsx(classes.dropZoneLabel, { [classes.dropZoneLabelDisabled]: disabled })} display="flex">
                        {label}
                        {linkedLabel && (
                            <span key="linked-label" className={clsx({ [classes.dropZoneLinkedLabel]: !disabled })}>
                                &nbsp;{linkedLabel}
                            </span>
                        )}
                        {required && <span key="required-indicator">&nbsp;*</span>}
                    </Box>
                </Box>
            </Box>
            <Box className={clsx(classes.uploadHelperText, { [classes.uploadHelperTextDisabled]: disabled })} height={32}>
                {helperText}
            </Box>
        </Box>
    );
};

const useStyles = makeStyles(() =>
    createStyles({
        dropZone: {
            backgroundColor: Color.grey1,
            borderRadius: "3px 3px 0 0",
            color: Color.grey6,
            minHeight: "120px",
            width: "100%",
        },
        dropZoneDragOver: {
            backgroundColor: Color.grey4,
        },
        dropZoneFocusAndHover: {
            "&:focus > div > svg, &:hover > div > svg": {
                color: Color.blue1,
            },
            "&:focus > div > div > span, &:hover > div > div > span": {
                color: Color.blue3,
            },
        },
        dropZoneTextures: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            margin: "auto 0",
            pointerEvents: "none", // prevent `dragLeave` while dragging over icon or label
        },
        dropZoneIcon: {
            color: Color.grey5,
            marginBottom: 8,
            // view box must be bigger to show icon in correct size
            height: "24px",
            width: "30px",
        },
        dropZoneIconDisabled: {
            color: Color.grey4,
            "&:hover": {
                color: Color.grey4,
            },
        },
        dropZoneLabel: {
            color: Color.grey6,
            fontSize: "14px",
            lineHeight: "14px",
            textDecoration: "none",
            whiteSpace: "nowrap",
        },
        dropZoneLabelDisabled: {
            color: Color.grey5,
        },
        dropZoneLinkedLabel: {
            color: Color.blue1,
            whiteSpace: "nowrap",
        },
        dropZonePointer: {
            cursor: "pointer",
        },
        dropZoneRequired: {
            backgroundColor: Color.grey2,
        },
        fileInputField: {
            display: "none",
        },
        uploadHelperText: {
            color: Color.grey6,
            padding: "5px 16px 0 16px",
        },
        uploadHelperTextDisabled: {
            color: Color.grey5,
        },
    }),
);
